import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import {
  VerifyTotpRequestSchema,
  type VerifyTotpRequest,
  type RegisterTotpResponse,
  type AccountSecurityStatusResponse
} from '@bliks-io/contracts';
import { Button, ConfirmDialog } from '@bliks-io/ui';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { FormInput } from '@/components/form/rhf';
import { useRegisterTotp, useVerifyTotp, useRemoveTotp } from '@/api/mutations/useTotp';
import type { PendingSensitiveAction, StepUpAction } from '@/lib/auth/stepUpReauth';

interface TwoFactorSectionProps {
  totp: AccountSecurityStatusResponse['totp'];
  pendingAction?: PendingSensitiveAction | null;
  /** Runs the action for a fresh session; a stale one opens the step-up dialog instead. */
  guardSensitive: (action: StepUpAction, run: () => void) => void;
  /** Opens SecurityTab's step-up dialog for the given interrupted action (403 backstop). */
  onStepUpRequired: (action: StepUpAction) => void;
}

/**
 * TOTP self-service: status view (enabled/disabled), QR + manual-secret
 * setup view, code verification, and removal with confirmation. The setup
 * state lives locally; a successful verify invalidates the security query,
 * which flips `totp.enabled` and returns this section to the status view.
 */
export function TwoFactorSection({
  totp,
  pendingAction,
  guardSensitive,
  onStepUpRequired
}: TwoFactorSectionProps) {
  const { t } = useTranslation();
  const registerTotp = useRegisterTotp({ onReauthRequired: onStepUpRequired });
  const verifyTotp = useVerifyTotp({ onReauthRequired: onStepUpRequired });
  const removeTotp = useRemoveTotp({ onReauthRequired: onStepUpRequired });

  const [pendingSetup, setPendingSetup] = useState<RegisterTotpResponse | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  // Destructive resume: after a step-up round trip only the ConfirmDialog
  // re-opens — the removal itself still needs a fresh confirm click.
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(pendingAction?.kind === 'totp-remove');

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting }
  } = useForm<VerifyTotpRequest>({
    resolver: zodResolver(VerifyTotpRequestSchema),
    defaultValues: { code: '' }
  });

  const startSetup = () =>
    registerTotp
      .mutateAsync()
      .then((setup) => {
        reset();
        setShowSecret(false);
        setPendingSetup(setup);
      })
      // Error feedback (toast or step-up dialog) comes from the mutation's
      // onError; a stale session 403s and routes back into the step-up dialog
      // via onReauthRequired.
      .catch(() => undefined);

  const cancelSetup = () => setPendingSetup(null);

  // Genuine external synchronization: auto-resume TOTP setup after a step-up
  // return fires a network mutation on mount. The ref guard keeps a StrictMode
  // double-mount from minting two registrations (the QR shown must match the
  // last-registered secret).
  const autoResumeStartedRef = useRef(false);
  useEffect(function resumeTotpSetupOnce() {
    if (pendingAction?.kind !== 'totp-register') return;
    if (autoResumeStartedRef.current) return;
    autoResumeStartedRef.current = true;
    void startSetup();
  });

  const onVerify = async (data: VerifyTotpRequest) => {
    // Errors toast via the mutation; stay in the setup view for a retry.
    await verifyTotp
      .mutateAsync(data)
      .then(() => setPendingSetup(null))
      .catch(() => undefined);
  };

  const onRemove = async () => {
    await removeTotp.mutateAsync().catch(() => undefined);
    setConfirmRemoveOpen(false);
  };

  if (pendingSetup) {
    return (
      <div className="space-y-4 border-t pt-6">
        <h3 className="text-sm font-medium">{t('userSettings.security.twoFactorTitle')}</h3>
        <p className="text-sm text-muted-foreground">{t('userSettings.security.scanQrCode')}</p>
        <div className="flex justify-center rounded-lg border bg-white p-4">
          <QRCodeSVG value={pendingSetup.uri} size={176} />
        </div>
        <div className="text-xs text-muted-foreground">
          <button
            type="button"
            className="underline"
            onClick={() => setShowSecret((current) => !current)}
          >
            {t('userSettings.security.manualSecret')}
          </button>
          {showSecret && (
            <code className="mt-2 block break-all rounded bg-muted p-2 font-mono">
              {pendingSetup.secret}
            </code>
          )}
        </div>
        <form noValidate onSubmit={handleSubmit(onVerify)} className="space-y-4">
          <FormInput
            control={control}
            name="code"
            label={t('userSettings.security.codeLabel')}
            placeholder="123456"
            autoComplete="one-time-code"
            required
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={cancelSetup}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting || verifyTotp.isPending}>
              {t('userSettings.security.verifyCode')}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-4 border-t pt-6">
      <h3 className="text-sm font-medium">{t('userSettings.security.twoFactorTitle')}</h3>
      {totp.enabled ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <span>{t('userSettings.security.twoFactorEnabled')}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              guardSensitive({ kind: 'totp-remove' }, () => setConfirmRemoveOpen(true))
            }
          >
            {t('userSettings.security.removeTwoFactor')}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {t('userSettings.security.twoFactorDescription')}
          </p>
          <Button
            type="button"
            onClick={() => guardSensitive({ kind: 'totp-register' }, () => void startSetup())}
            loading={registerTotp.isPending}
          >
            {t('userSettings.security.setupTwoFactor')}
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        icon={<ShieldOff className="h-5 w-5" />}
        iconTone="danger"
        title={t('userSettings.security.removeTwoFactor')}
        description={t('userSettings.security.removeTwoFactorConfirm')}
        confirmLabel={t('userSettings.security.removeTwoFactor')}
        onConfirm={() => void onRemove()}
        variant="danger"
        isPending={removeTotp.isPending}
      />
    </div>
  );
}
